/*
 * Purpose: LinkedIn OAuth callback — exchange code and return to compose
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import {
  exchangeLinkedInCode,
  parseOAuthState,
  persistLinkedInToken,
} from '@/services/connectors/linkedinOAuth';
import { toast } from 'sonner';

export default function LinkedInOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error_description') || searchParams.get('error');

    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError('Missing authorization code from LinkedIn.');
      return;
    }

    const { valid, returnPath } = parseOAuthState(state);
    if (!valid) {
      setError('OAuth state mismatch — try Connect LinkedIn again.');
      return;
    }

    void (async () => {
      try {
        const token = await exchangeLinkedInCode(code);
        persistLinkedInToken(token.access_token);
        toast.success('LinkedIn connected');
        navigate(returnPath, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'LinkedIn connection failed');
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">LinkedIn sign-in</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Completing connection…
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
